import { useCallMetadata } from 'components/calls/call_metadata/use_call_metadata'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'

import { css } from 'design'
import { Button } from 'primitives/button'
import { cn } from 'util/cn'

import Stopwatch from '../../../../controls/stopwatch'
import { useTimeDisplay } from '../../../../hooks/use_time_display'
import { CallStartIcon } from '../../../../images'
import { isLiveChatApp } from '../../../../reduxes/slices/live_chat_app/selectors'
import { useSelectorDelegateStore } from '../../../../reduxes/stores/root_store/hooks'
import { useCallConnectionMessage } from '../../../calls/call_metadata/use_call_connection_message'
import { BLANK_LABELS, useCallAction } from '../../../calls/use_call_action'
import { Timestamp } from '../../../timekeeping/timestamp'
import Timeline from '../../timeline'

export default function Call({ call, timeline }) {
  const connectionMessage = useCallConnectionMessage()
  const { callData, localParticipantData } = useCallMetadata(call)
  const { onClick, label, icon, disabled } = useCallAction(timeline?.topic_id)
  const { startTime, durationInSeconds, peopleOnCall, peopleThatJoinedCall } =
    callData
  const { formattedValue } = useTimeDisplay(durationInSeconds)
  const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

  const { hasParticipated } = localParticipantData

  const callHeading = useMemo(() => {
    if (!call.end_time) {
      return connectionMessage
    } else {
      return hasParticipated ? 'Call ended' : 'Missed Call'
    }
  }, [call.end_time, connectionMessage, hasParticipated])

  let heading = callHeading
  if (!liveChatApp) {
    heading += `- ${peopleThatJoinedCall?.join(
      ', ',
    )} (Lasted ${formattedValue})`
  }

  return (
    <Timeline
      timeline={timeline}
      item={call}
      handle={call.caller_handle}
      align="left"
      withActionsBar={false}
      withTimelineInfo={false}
      avatar={
        call.end_time ? (
          <div className="timeline__status__line topic-avatar" />
        ) : (
          <div className={callIconContainerStyles()}>
            <i>
              <CallStartIcon />
            </i>
          </div>
        )
      }
      className={cn(call.end_time && 'timeline__status')}
    >
      {call.end_time ? (
        <section>
          {heading}
          <Timestamp
            date={call.created_at}
            className="timeline__status__timestamp"
          />
        </section>
      ) : (
        <section className={timelineStyles()}>
          <div className={infoStyles()}>
            <h2>
              <span>
                {callHeading}&nbsp;
                <span className={stopwatchStyles()}>
                  <Stopwatch timeStart={startTime} />
                </span>
              </span>
            </h2>
            <h3>{peopleOnCall?.join(', ')}</h3>
          </div>
          {!BLANK_LABELS.includes(label) && (
            <Button
              className={actionBtnStyles()}
              onClick={onClick}
              disabled={disabled}
            >
              {icon}
              {`
          ${label} Call`}
            </Button>
          )}
        </section>
      )}
    </Timeline>
  )
}

Call.propTypes = {
  timeline: PropTypes.object.isRequired,
}

const timelineStyles = css({
  display: 'flex',
  flexDirection: 'column',
  color: '$grey700',
  border: '1px solid $grey100',
  borderRadius: '$3',
  width: '90%',
  padding: '$2 $3',

  '@md': {
    flexDirection: 'row',
    alignItems: 'center',
  },
})

const infoStyles = css({
  flexGrow: 1,
  marginBottom: '$2',

  '@md': {
    marginBottom: 0,
  },

  h2: {
    fontSize: '$3',
    fontWeight: 500,
    color: '$grey700',
  },

  h3: {
    fontSize: '$1',
    color: '$grey500',
    fontWeight: 'normal',
  },
})

const actionBtnStyles = css({
  height: 'min-content',
  padding: '$1 $5 $1 $4',
  whiteSpace: 'nowrap',

  svg: {
    marginRight: '$2',
  },
})

const callIconContainerStyles = css({
  minWidth: 42,
  height: 42,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: '$grey300',
  borderRadius: '$3',

  path: {
    fill: 'white',
  },
})

const stopwatchStyles = css({
  color: '$grey500',
  fontWeight: 400,
})
